import React from 'react';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { FiX, FiPrinter } from 'react-icons/fi';

export default function QRLabelModal({ asset, onClose }) {
  const url = `${window.location.origin}/app/assets/${asset.id}?from=qr`;

  const handlePrint = () => {
    const svgEl = document.getElementById('qr-svg-single');
    const svgData = svgEl ? svgEl.outerHTML : '';
    const w = window.open('', '_blank', 'width=400,height=500');
    w.document.write(`<!DOCTYPE html><html><head><title>QR Label - ${asset.tag}</title>
      <style>
        body { margin: 0; display: flex; align-items: center; justify-content: center; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
        .label { text-align: center; padding: 24px; border: 2px solid #e2e8f0; border-radius: 12px; width: 280px; }
        .tag { font-size: 22px; font-weight: 800; margin-bottom: 4px; }
        .name { font-size: 14px; color: #64748b; margin-bottom: 16px; }
        .qr { margin: 0 auto; }
        .url { font-size: 9px; color: #94a3b8; margin-top: 12px; word-break: break-all; }
        @media print { body { margin: 0; } .label { border: 1px solid #ccc; } }
      </style></head><body>
      <div class="label">
        <div class="tag">${asset.tag}</div>
        <div class="name">${asset.name}</div>
        <div class="qr">${svgData}</div>
        <div class="url">${url}</div>
      </div>
      <script>window.onload=function(){window.print();}<\/script>
    </body></html>`);
    w.document.close();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200
      }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)', borderRadius: 20, padding: 28, width: '90%', maxWidth: 380,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>QR Label</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
            <FiX size={20} />
          </button>
        </div>

        <div style={{
          padding: 24, borderRadius: 12, border: '2px solid var(--border)', marginBottom: 20
        }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>{asset.tag}</div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>{asset.name}</div>
          <div id="qr-svg-single" style={{ display: 'inline-block' }}>
            <QRCodeSVG value={url} size={180} level="M" />
          </div>
          <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 12, wordBreak: 'break-all' }}>{url}</div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: 12, borderRadius: 10, border: '1px solid var(--border)',
            background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600, cursor: 'pointer'
          }}>Close</button>
          <motion.button
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={handlePrint}
            style={{
              flex: 1, padding: 12, borderRadius: 10, border: 'none',
              background: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
            }}
          >
            <FiPrinter size={16} /> Print
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Bulk print function - generates a print window with multiple QR labels
export function printBulkQRLabels(assets) {
  const origin = window.location.origin;
  const labels = assets.map(a => {
    const url = `${origin}/assets/${a.id}`;
    return `<div class="label">
      <div class="tag">${a.tag}</div>
      <div class="name">${a.name}</div>
      <div class="qr" id="qr-${a.id}"></div>
      <div class="url">${url}</div>
    </div>`;
  }).join('');

  const w = window.open('', '_blank', 'width=800,height=600');
  w.document.write(`<!DOCTYPE html><html><head><title>QR Labels</title>
    <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js"><\/script>
    <style>
      body { margin: 20px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
      .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 16px; }
      .label { text-align: center; padding: 16px; border: 2px solid #e2e8f0; border-radius: 12px; page-break-inside: avoid; }
      .tag { font-size: 18px; font-weight: 800; margin-bottom: 2px; }
      .name { font-size: 12px; color: #64748b; margin-bottom: 12px; }
      .qr canvas { display: block; margin: 0 auto; }
      .url { font-size: 8px; color: #94a3b8; margin-top: 8px; word-break: break-all; }
      @media print { body { margin: 0; } .grid { gap: 8px; } }
    </style></head><body>
    <div class="grid">${labels}</div>
    <script>
      ${assets.map(a => `QRCode.toCanvas(document.querySelector('#qr-${a.id}'), '${origin}/assets/${a.id}', {width:150,margin:1}, function(){});`).join('\n')}
      setTimeout(function(){window.print();}, 500);
    <\/script>
  </body></html>`);
  w.document.close();
}
